import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Upload, FileText, Check, Send, X, Download } from "lucide-react";
import { api, API, BACKEND, APP_TYPE_LABELS, INTERNAL_STAGE_LABELS, DOC_TYPE_LABELS, fmtDDMMYYYY, fmtGBP } from "../../api";
import { PageHeader, Panel, Btn, Pill, Input, Select, TextArea, Empty } from "../../ui";

const STAGES = ["new_enquiry", "information_collection", "documents_outstanding", "ready_for_broker", "sent_to_broker", "broker_review", "application_progressing", "offer_received", "completion_pending", "completed"];
const EXCEPTIONS = ["on_hold", "withdrawn", "declined"];
const TABS = ["overview", "applicants", "form", "documents", "handover", "progress", "tasks", "messages", "fees", "activity"];

const Tab = ({ id, label, active, onClick }) => (
  <button onClick={onClick} data-testid={`case-tab-${id}`}
    className={`px-4 py-2 text-[11px] font-bold tracking-[0.14em] uppercase border-b-2 whitespace-nowrap ${active ? "border-slate-800 text-slate-900" : "border-transparent text-slate-500 hover:text-slate-800"}`}>
    {label}
  </button>
);

export default function ApplicationDetailPage() {
  const { id } = useParams();
  const [app, setApp] = useState(null);
  const [tab, setTab] = useState("overview");
  const [docs, setDocs] = useState([]);
  const [brokers, setBrokers] = useState([]);
  const [messages, setMessages] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [activity, setActivity] = useState([]);
  const [fees, setFees] = useState([]);
  const [handoverPurpose, setHandoverPurpose] = useState("");
  const [handoverBroker, setHandoverBroker] = useState("");
  const [handoverDocs, setHandoverDocs] = useState([]);
  const [messageBody, setMessageBody] = useState("");
  const [messageScope, setMessageScope] = useState("internal");

  const refresh = async () => {
    const a = await api.get(`${API}/applications/${id}`);
    setApp(a.data);
    const [d, b, m, t, act, ff] = await Promise.all([
      api.get(`${API}/doc-requests?case_id=${id}`),
      api.get(`${API}/users?role=broker`),
      api.get(`${API}/messages?case_id=${id}`),
      api.get(`${API}/tasks`),
      api.get(`${API}/activity?limit=100`),
      api.get(`${API}/fees`),
    ]);
    setDocs(d.data); setBrokers(b.data); setMessages(m.data);
    setTasks(t.data.filter(x => x.case_id === id));
    setActivity(act.data.filter(x => x.target_id === id || (x.meta && x.meta.case_id === id)));
    setFees(ff.data.filter(x => x.case_id === id));
  };
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [id]);

  if (!app) return <p className="text-sm text-slate-500">Loading…</p>;

  const changeStage = async (val) => {
    await api.put(`${API}/applications/${id}`, { internal_status: val });
    toast.success("Stage updated"); refresh();
  };
  const changeFollowup = async (val) => {
    await api.put(`${API}/applications/${id}`, { follow_up_date: val });
    toast.success("Follow-up updated"); refresh();
  };
  const changeCustomerStatus = async (val) => {
    await api.put(`${API}/applications/${id}`, { customer_status: val });
    toast.success("Customer status saved");
  };

  const uploadToRequest = async (reqId, file) => {
    const fd = new FormData(); fd.append("file", file);
    try {
      await api.post(`${API}/doc-requests/${reqId}/upload`, fd);
      toast.success("Uploaded");
      refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
  };
  const review = async (reqId, status) => {
    await api.put(`${API}/doc-requests/${reqId}/review`, { status });
    toast.success("Reviewed"); refresh();
  };
  const createHandover = async () => {
    try {
      await api.post(`${API}/handovers`, {
        case_id: id,
        broker_user_id: handoverBroker,
        include_doc_ids: handoverDocs,
        purpose: handoverPurpose || "Case handover for broker review",
      });
      toast.success("Handover created and recorded");
      refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not create handover"); }
  };
  const postMessage = async () => {
    if (!messageBody.trim()) return;
    try {
      await api.post(`${API}/messages`, { case_id: id, scope: messageScope, body: messageBody });
      setMessageBody("");
      toast.success("Message posted");
      refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not post"); }
  };
  const openFile = async (fid) => {
    try {
      const { data } = await api.post(`${API}/doc-files/${fid}/download`);
      window.open(`${BACKEND}/api/agency/doc-files/${fid}/stream?token=${data.token}`, "_blank");
    } catch (e) { toast.error(e.response?.data?.detail || "Not allowed"); }
  };

  return (
    <div>
      <PageHeader title={`${app.ref} — ${APP_TYPE_LABELS[app.type]}`} subtitle={`Owner: staff · Follow-up ${fmtDDMMYYYY(app.follow_up_date)}`} action={<Link to="/agency/applications" className="text-[12px] text-slate-500 hover:text-slate-800">← Back</Link>} />

      <div className="flex gap-1 border-b border-slate-200 overflow-x-auto mb-6">
        {TABS.map((t) => <Tab key={t} id={t} label={t[0].toUpperCase() + t.slice(1)} active={tab === t} onClick={() => setTab(t)} />)}
      </div>

      {tab === "overview" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <Panel className="p-5 space-y-3">
            <h3 className="font-serif text-lg">Case fields</h3>
            <div className="grid grid-cols-2 gap-3 text-[13px]">
              <div><p className="text-[10px] uppercase tracking-[0.14em] text-slate-500">Ref</p><p className="font-mono text-slate-800">{app.ref}</p></div>
              <div><p className="text-[10px] uppercase tracking-[0.14em] text-slate-500">Type</p><p>{APP_TYPE_LABELS[app.type]}{app.subtype && ` · ${app.subtype}`}</p></div>
              <div><p className="text-[10px] uppercase tracking-[0.14em] text-slate-500">Internal stage</p>
                <select className="border border-slate-300 rounded px-2 py-1 text-[13px]" value={app.internal_status} onChange={(e) => changeStage(e.target.value)} data-testid="case-stage-select">
                  {STAGES.map(s => <option key={s} value={s}>{INTERNAL_STAGE_LABELS[s]}</option>)}
                  <option disabled>──</option>
                  {EXCEPTIONS.map(s => <option key={s} value={s}>{INTERNAL_STAGE_LABELS[s]}</option>)}
                </select>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-[0.14em] text-slate-500">Follow-up date</p>
                <input type="date" className="border border-slate-300 rounded px-2 py-1 text-[13px]" value={app.follow_up_date || ""} onChange={(e) => changeFollowup(e.target.value)} data-testid="case-followup" />
              </div>
            </div>
          </Panel>
          <Panel className="p-5 space-y-3">
            <h3 className="font-serif text-lg">Customer-facing status</h3>
            <p className="text-[12px] text-slate-500">What the customer sees in their portal. Keep internal notes out.</p>
            <textarea rows={5} className="w-full border border-slate-300 rounded p-2 text-[13px]" defaultValue={app.customer_status || ""} onBlur={(e) => changeCustomerStatus(e.target.value)} data-testid="case-customer-status" />
            <p className="text-[11px] text-slate-500">Milestones recorded by the broker: {app.milestones?.length || 0}</p>
          </Panel>
        </div>
      )}

      {tab === "documents" && (
        <Panel>
          <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
            <p className="text-[12px] text-slate-500">Agency completeness checks remain separate from broker acceptance.</p>
            <button onClick={async () => {
              try {
                const { data } = await api.post(`${API}/applications/${id}/apply-checklist`);
                toast.success(data.created ? `Added ${data.created} documents from the template` : "Checklist already applied");
                refresh();
              } catch (e) { toast.error(e.response?.data?.detail || "Could not apply checklist"); }
            }} data-testid="apply-checklist" className="inline-flex items-center gap-1 text-[11px] font-bold tracking-[0.14em] uppercase text-slate-700 hover:text-slate-900">
              Apply checklist template
            </button>
          </div>
          <table className="w-full text-sm">
            <thead><tr className="border-b border-slate-200 text-[10.5px] tracking-[0.14em] uppercase text-slate-500 bg-slate-50">
              <th className="text-left px-5 py-3">Document</th><th className="text-left px-5 py-3">Related</th><th className="text-left px-5 py-3">Period</th><th className="text-left px-5 py-3">Status</th><th className="text-left px-5 py-3">Broker</th><th className="text-left px-5 py-3">Files</th><th />
            </tr></thead>
            <tbody>
              {docs.map((d, i) => (
                <tr key={d.id} className="border-b border-slate-100">
                  <td className="px-5 py-3 font-medium">{DOC_TYPE_LABELS[d.doc_type] || d.doc_type}</td>
                  <td className="px-5 py-3 text-slate-600">{d.related_ref?.label || "—"}</td>
                  <td className="px-5 py-3 text-slate-600">{d.period || "—"}</td>
                  <td className="px-5 py-3"><Pill tone={d.status === "agency_checked" ? "emerald" : d.status === "replacement_required" ? "rose" : d.status === "uploaded" ? "sky" : "amber"}>{d.status.replace(/_/g, " ")}</Pill></td>
                  <td className="px-5 py-3"><Pill tone={d.broker_status === "shared" ? "sky" : d.broker_status === "accepted" ? "emerald" : "slate"}>{d.broker_status.replace(/_/g, " ")}</Pill></td>
                  <td className="px-5 py-3 text-[12px] text-slate-600">
                    {d.files.length === 0 ? "—" : d.files.map((fid) => <button key={fid} onClick={() => openFile(fid)} className="underline mr-2" data-testid={`doc-file-${fid}`}>file</button>)}
                  </td>
                  <td className="px-5 py-3 space-x-1 whitespace-nowrap">
                    <label className="inline-flex items-center gap-1 px-2 py-1 border border-slate-300 rounded cursor-pointer hover:border-slate-500 text-[11px]"><Upload size={11} /> Upload<input type="file" className="hidden" onChange={(e) => e.target.files[0] && uploadToRequest(d.id, e.target.files[0])} /></label>
                    {d.status === "uploaded" && <button onClick={() => review(d.id, "agency_checked")} className="inline-flex items-center gap-1 px-2 py-1 border border-emerald-500 text-emerald-700 rounded text-[11px] hover:bg-emerald-50"><Check size={11} /> Checked</button>}
                    {d.status === "uploaded" && <button onClick={() => review(d.id, "replacement_required")} className="inline-flex items-center gap-1 px-2 py-1 border border-rose-500 text-rose-700 rounded text-[11px] hover:bg-rose-50"><X size={11} /> Replace</button>}
                  </td>
                </tr>
              ))}
              {docs.length === 0 && <tr><td colSpan={7} className="px-5 py-8 text-center text-sm text-slate-500">No document requests for this case.</td></tr>}
            </tbody>
          </table>
        </Panel>
      )}

      {tab === "handover" && (
        <Panel className="p-5 space-y-4">
          <h3 className="font-serif text-lg">Create broker handover (dated snapshot)</h3>
          <p className="text-[12px] text-slate-500">Nothing is auto-shared with brokers. Review and select exactly what to send.</p>
          <Select label="Assign broker" value={handoverBroker} onChange={(e) => setHandoverBroker(e.target.value)} testid="handover-broker">
            <option value="">— Pick broker —</option>
            {brokers.map((b) => <option key={b.id} value={b.id}>{b.name}{b.broker_firm_name ? ` — ${b.broker_firm_name}` : ""}</option>)}
          </Select>
          <Input label="Purpose / note" value={handoverPurpose} onChange={(e) => setHandoverPurpose(e.target.value)} testid="handover-purpose" />
          <div>
            <p className="text-[11px] font-bold tracking-[0.14em] uppercase text-slate-500 mb-2">Documents to include</p>
            <div className="space-y-1 max-h-56 overflow-y-auto border border-slate-200 rounded p-2">
              {docs.flatMap(d => d.files.map(fid => ({ fid, label: `${DOC_TYPE_LABELS[d.doc_type] || d.doc_type} — ${d.related_ref?.label || ""}` }))).map(({ fid, label }) => (
                <label key={fid} className="flex items-center gap-2 text-[13px]">
                  <input type="checkbox" checked={handoverDocs.includes(fid)} onChange={(e) => setHandoverDocs(e.target.checked ? [...handoverDocs, fid] : handoverDocs.filter(x => x !== fid))} />
                  {label}
                </label>
              ))}
              {docs.every(d => d.files.length === 0) && <p className="text-[12px] text-slate-500 text-center py-4">No uploaded documents yet.</p>}
            </div>
          </div>
          <div className="flex justify-end"><Btn onClick={createHandover} disabled={!handoverBroker} data-testid="handover-send"><Send size={13} /> Send to broker</Btn></div>
        </Panel>
      )}

      {tab === "messages" && (
        <Panel className="p-5 space-y-4">
          <h3 className="font-serif text-lg">Messages</h3>
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {messages.map((m) => (
              <div key={m.id} className="border border-slate-200 rounded p-3">
                <div className="flex items-center justify-between">
                  <Pill tone={m.scope === "internal" ? "slate" : m.scope === "customer" ? "sky" : "violet"}>{m.scope}</Pill>
                  <p className="text-[11px] text-slate-500">{fmtDDMMYYYY(m.created_at)}{!m.published && " · pending publish"}</p>
                </div>
                <p className="text-[13px] text-slate-700 mt-2 whitespace-pre-wrap">{m.body}</p>
              </div>
            ))}
            {messages.length === 0 && <p className="text-[13px] text-slate-500 text-center py-4">No messages yet.</p>}
          </div>
          <div className="border-t border-slate-200 pt-4 space-y-2">
            <Select label="Scope" value={messageScope} onChange={(e) => setMessageScope(e.target.value)}>
              <option value="internal">Internal (agency only)</option>
              <option value="customer">Visible to customer</option>
              <option value="broker">Visible to broker</option>
            </Select>
            <TextArea rows={3} value={messageBody} onChange={(e) => setMessageBody(e.target.value)} placeholder="Write a message…" />
            <div className="flex justify-end"><Btn onClick={postMessage} data-testid="msg-post">Post</Btn></div>
          </div>
        </Panel>
      )}

      {tab === "tasks" && (
        <Panel className="p-5">
          <h3 className="font-serif text-lg mb-3">Tasks for this case</h3>
          <ul className="divide-y divide-slate-100">
            {tasks.map((t) => <li key={t.id} className="py-2 flex items-center justify-between"><span className="text-sm">{t.title}</span><span className="text-[11px] text-slate-500">Due {fmtDDMMYYYY(t.due_date)}</span></li>)}
            {tasks.length === 0 && <li className="py-4 text-sm text-slate-500 text-center">No tasks.</li>}
          </ul>
        </Panel>
      )}

      {tab === "fees" && (
        <Panel className="p-5">
          <h3 className="font-serif text-lg mb-3">Referral fees</h3>
          <ul className="divide-y divide-slate-100">
            {fees.map((f) => <li key={f.id} className="py-2 flex items-center justify-between text-[13px]"><span>Expected {fmtGBP(f.expected)} · Trigger: {f.trigger}</span><span className="text-slate-500">Received {fmtGBP(f.received_amount || 0)}</span></li>)}
            {fees.length === 0 && <li className="py-4 text-sm text-slate-500 text-center">No fees yet.</li>}
          </ul>
        </Panel>
      )}

      {tab === "activity" && (
        <Panel className="p-5">
          <h3 className="font-serif text-lg mb-3">Audit history (this case)</h3>
          <ul className="divide-y divide-slate-100 text-[12px]">
            {activity.map((a) => <li key={a.id} className="py-2 flex items-center justify-between"><span className="font-mono">{a.action}</span><span className="text-slate-500">{fmtDDMMYYYY(a.created_at)}</span></li>)}
            {activity.length === 0 && <li className="py-4 text-sm text-slate-500 text-center">Nothing yet.</li>}
          </ul>
        </Panel>
      )}

      {(tab === "applicants" || tab === "form" || tab === "progress") && (
        <Panel className="p-6">
          <p className="text-[13px] text-slate-500">Draft answers (saved by customer in their portal):</p>
          <pre className="mt-3 bg-slate-50 border border-slate-200 rounded p-3 text-[12px] whitespace-pre-wrap">{JSON.stringify(app.answers_draft || {}, null, 2)}</pre>
        </Panel>
      )}
    </div>
  );
}
